<template>
  <div class="schedule-detail-page">
    <div class="container">
      <h1 class="title">Schedule Details</h1>
      <h2 class="subtitle" v-if="site.name">{{ site.name }}</h2>

      <div class="box">
        <div class="level">
          <div class="level-left">
            <div class="level-item">
              <router-link :to="`/sites/${schedule.site_id}/schedules`" class="button is-info">
                <span class="icon"><i class="bx bx-arrow-left"></i></span>
                <span>Back to Schedules</span>
              </router-link>
            </div>
          </div>
          <div class="level-right">
            <div class="level-item">
              <div class="buttons">
                <router-link :to="`/schedules/${scheduleId}/edit`" class="button is-warning">
                  <span class="icon"><i class="bx bx-edit"></i></span>
                  <span>Edit Schedule</span>
                </router-link>
                <button
                    v-if="!schedule.is_active"
                    @click="activateSchedule()"
                    class="button is-success"
                >
                  <span class="icon"><i class="bx bx-check"></i></span>
                  <span>Set as Active</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="loading" class="has-text-centered my-5">
          <span class="icon is-large"><i class="bx bx-loader bx-spin bx-lg"></i></span>
          <p>Loading schedule data...</p>
        </div>

        <div v-else>
          <!-- Status Badge -->
          <div class="tags has-addons mb-5">
            <span class="tag is-medium">Status</span>
            <span
                class="tag is-medium"
                :class="schedule.is_active ? 'is-success' : 'is-light'"
            >
              {{ schedule.is_active ? 'Active' : 'Inactive' }}
            </span>
          </div>

          <!-- Calendar View -->
          <div class="columns">
            <div class="column is-8">
              <!-- Working Days Schedule -->
              <div class="card mb-5">
                <header class="card-header">
                  <p class="card-header-title">
                    <span class="icon mr-2"><i class="bx bx-calendar-week"></i></span>
                    Weekly Schedule
                  </p>
                </header>
                <div class="card-content">
                  <table class="table is-fullwidth">
                    <thead>
                    <tr>
                      <th>Day</th>
                      <th>Status</th>
                      <th>Hours</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="day in sortedWorkingDays" :key="day.id">
                      <td>{{ day.day_name }}</td>
                      <td>
                          <span
                              class="tag"
                              :class="day.is_working ? 'is-success' : 'is-light'"
                          >
                            {{ day.is_working ? 'Working' : 'Closed' }}
                          </span>
                      </td>
                      <td>
                          <span v-if="day.is_working && day.open_time && day.close_time">
                            {{ formatTime(day.open_time) }} - {{ formatTime(day.close_time) }}
                          </span>
                        <span v-else-if="day.is_working">All Day</span>
                        <span v-else class="has-text-grey">-</span>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Working Months -->
              <div class="card mb-5">
                <header class="card-header">
                  <p class="card-header-title">
                    <span class="icon mr-2"><i class="bx bx-calendar"></i></span>
                    Working Months
                  </p>
                </header>
                <div class="card-content">
                  <div class="tags">
                    <span
                        v-for="month in sortedWorkingMonths"
                        :key="month.id"
                        class="tag is-medium mr-2 mb-2"
                        :class="month.is_working ? 'is-info' : 'is-light'"
                    >
                      {{ month.month_name }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Meal Service Times -->
              <div class="card mb-5">
                <header class="card-header">
                  <p class="card-header-title">
                    <span class="icon mr-2"><i class="bx bx-restaurant"></i></span>
                    Meal Service Times
                  </p>
                </header>
                <div class="card-content">
                  <div v-if="!sortedMealServiceTimes.length" class="notification is-light">
                    No meal service times defined
                  </div>
                  <table v-else class="table is-fullwidth">
                    <thead>
                      <tr>
                        <th>Day</th>
                        <th>Meal Type</th>
                        <th>Service Time</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="mealTime in sortedMealServiceTimes" :key="mealTime.id">
                        <td>{{ mealTime.day_name }}</td>
                        <td>{{ mealTime.meal_type?.name || 'Unknown' }}</td>
                        <td>{{ formatTime(mealTime.start_time) }} - {{ formatTime(mealTime.end_time) }}</td>
                        <td>
                          <span
                            class="tag"
                            :class="mealTime.is_active ? 'is-success' : 'is-light'"
                          >
                            {{ mealTime.is_active ? 'Active' : 'Inactive' }}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div class="column is-4">
              <!-- Holidays -->
              <div class="card">
                <header class="card-header">
                  <p class="card-header-title">
                    <span class="icon mr-2"><i class="bx bx-beach"></i></span>
                    Holidays
                  </p>
                </header>
                <div class="card-content">
                  <div v-if="schedule.holidays.length === 0" class="notification is-light">
                    No holidays defined
                  </div>
                  <table v-else class="table is-fullwidth">
                    <thead>
                    <tr>
                      <th>Name</th>
                      <th>Date</th>
                      <th>Recurring</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="holiday in sortedHolidays" :key="holiday.id">
                      <td>{{ holiday.name }}</td>
                      <td>{{ formatDate(holiday.date) }}</td>
                      <td>
                          <span
                              class="tag"
                              :class="holiday.is_recurring ? 'is-warning' : 'is-light'"
                          >
                            {{ holiday.is_recurring ? 'Yes' : 'No' }}
                          </span>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapActions } from 'vuex';

export default {
  name: 'SiteScheduleShow',

  data() {
    return {
      scheduleId: null,
      schedule: {
        working_days: [],
        working_months: [],
        holidays: [],
        meal_service_times: []
      },
      site: {},
      loading: true
    };
  },

  computed: {
    sortedWorkingDays() {
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      return [...this.schedule.working_days]
          .sort((a, b) => a.day_of_week - b.day_of_week)
          .map(day => ({
            ...day,
            day_name: dayNames[day.day_of_week % 7]
          }));
    },

    sortedWorkingMonths() {
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      return [...this.schedule.working_months]
          .filter(month => month.is_working)
          .sort((a, b) => a.month - b.month)
          .map(month => ({
            ...month,
            month_name: monthNames[month.month - 1]
          }));
    },

    sortedHolidays() {
      return [...this.schedule.holidays].sort((a, b) => new Date(a.date) - new Date(b.date));
    },

    sortedMealServiceTimes() {
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      return [...(this.schedule.meal_service_times || [])]
        .filter(time => time.is_active)
        .sort((a, b) => {
          // First sort by day of week
          if (a.day_of_week !== b.day_of_week) {
            return a.day_of_week - b.day_of_week;
          }
          // Then sort by start time
          return a.start_time.localeCompare(b.start_time);
        })
        .map(time => ({
          ...time,
          day_name: dayNames[time.day_of_week % 7]
        }));
    }
  },

  created() {
    this.scheduleId = this.$route.params.scheduleId;
    this.fetchSchedule();
  },

  methods: {
    ...mapActions({
      showNotification: 'notifications/showNotification'
    }),

    async fetchSchedule() {
      try {
        this.loading = true;

        const response = await axios.get(`/api/schedules/${this.scheduleId}`);
        this.schedule = response.data.data;

        // Fetch site details
        const siteResponse = await axios.get(`/api/sites/${this.schedule.site_id}`);
        this.site = siteResponse.data;
      } catch (error) {
        this.showNotification({
          type: 'danger',
          message: 'Failed to load schedule: ' + (error.response?.data?.message || error.message)
        });
      } finally {
        this.loading = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },

    formatTime(timeString) {
      if (!timeString) return '';

      // Handle different time formats
      const timeParts = timeString.split(':');
      if (timeParts.length < 2) return timeString;

      let hours = parseInt(timeParts[0]);
      const minutes = timeParts[1];
      const ampm = hours >= 12 ? 'PM' : 'AM';

      hours = hours % 12;
      hours = hours ? hours : 12; // The hour '0' should be '12'

      return `${hours}:${minutes} ${ampm}`;
    },

    async activateSchedule() {
      try {
        await axios.patch(`/api/schedules/${this.scheduleId}/toggle-active`);
        this.showNotification({
          type: 'success',
          message: 'Schedule activated successfully'
        });
        this.fetchSchedule(); // Refresh data
      } catch (error) {
        this.showNotification({
          type: 'danger',
          message: 'Failed to activate schedule: ' + (error.response?.data?.message || error.message)
        });
      }
    }
  }
};
</script>
